'use client';

import { UploadCloud, X } from 'lucide-react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

// Импортируй наш Server Action для сохранения


import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { saveUploadRecord } from '../../(tourism)/tourism/lib/actions/tourismActions';

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

type UniversalUploadProps = {
  dialogTitle: string;
  buttonText: string;
  // endpoint больше не нужен, мы используем универсальный API presign
  showCategorySelect?: boolean;
  categories?: { key: string; label: string }[];
  uploadType: 'reporter' | 'tourism';
};

export function UniversalUpload({
  dialogTitle,
  buttonText,
  showCategorySelect = false,
  categories = [],
  uploadType,
}: UniversalUploadProps) {
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const formRef = useRef<HTMLFormElement>(null);
  const router = useRouter();

  // Очистка URL
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      setError(`Файл слишком большой (макс. ${MAX_FILE_SIZE_MB} МБ)`);
      setFile(null);
      return;
    }
    if (!selectedFile.type.startsWith('image/')) {
      setError('Неверный тип файла. Пожалуйста, выберите изображение.');
      setFile(null);
      return;
    }

    setError(null);
    setFile(selectedFile);
  };

  const handleClearFile = useCallback(() => {
    setFile(null);
    setError(null);
    if (formRef.current) {
      const fileInput = formRef.current.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    }
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!file) {
        setError('Пожалуйста, выберите файл для загрузки.');
        return;
      }
      setIsUploading(true);
      const toastId = toast.loading('Загрузка...');

      try {
        const formData = new FormData(event.currentTarget);

        // --- 1. Получаем Presigned URL ---
        const presignRes = await fetch('/api/upload-photo/presign', {
          method: 'POST',
          body: JSON.stringify({
            contentType: file.type,
            uploadType,
          }),
        });

        if (!presignRes.ok) throw new Error('Ошибка получения ссылки загрузки');
        const { uploadUrl, fileUrl, filename } = await presignRes.json();

        // --- 2. Грузим файл в облако (напрямую) ---
        const uploadRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });

        if (!uploadRes.ok) throw new Error('Ошибка загрузки файла в облако');

        // --- 3. Сохраняем метаданные в БД через Server Action ---
        // Собираем данные формы
        const caption = formData.get('caption') as string;
        const description = formData.get('description') as string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const category = formData.get('category') as any; // Приведение типа для Enum
        const endDate = (formData.get('endDate') as string) || undefined;
        // Вызываем наш универсальный Server Action
        const result = await saveUploadRecord({
          fileUrl,
          publicId: filename,
          uploadType,
          caption: uploadType === 'reporter' ? caption : undefined, // для репортера
          title: uploadType === 'tourism' ? caption : undefined, // для туризма (поле name=caption используется как title)
          description,
          category,
          endDate,
        });

        toast.success(result.message, { id: toastId });
        setOpen(false);
        setSelectedCategory('');
        handleClearFile();
        formRef.current?.reset();
        router.refresh();
      } catch (err) {
        console.error(err);
        toast.error('Произошла ошибка загрузки', { id: toastId });
      } finally {
        setIsUploading(false);
      }
    },
    [file, uploadType, router, handleClearFile],
  );

  const inputStyles = `
    mt-2 bg-slate-100/80 dark:bg-slate-900/60 
    border border-slate-300 dark:border-slate-700 
    focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-0 
    dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400
  `;

  const selectStyles = `
    mt-2 block w-full rounded-md p-2 text-sm
    bg-slate-100/80 dark:bg-slate-900/60 
    border border-slate-300 dark:border-slate-700 
    focus:ring-2 focus:ring-sky-500 focus:outline-none
    dark:text-slate-100
  `;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="flex items-center gap-2">
          <UploadCloud size={18} /> {buttonText}
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[480px] shadow-lg bg-white/80 backdrop-blur-md dark:bg-slate-800/80 dark:backdrop-blur-sm dark:border dark:border-slate-700 dark:text-slate-200">
        <DialogHeader>
          <DialogTitle className="text-slate-900 dark:text-white">{dialogTitle}</DialogTitle>
        </DialogHeader>

        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 pt-2">
          {/* Поле uploadType больше не нужно скрытым инпутом, оно в пропсах */}

          <div>
            <label className="font-medium text-sm text-slate-800 dark:text-slate-100">
              Фотография
            </label>
            {previewUrl ? (
              <div className="mt-2 relative h-60 w-full">
                <Image
                  src={previewUrl}
                  alt={`Предпросмотр: ${file?.name}`}
                  fill
                  className="object-contain rounded-md border border-slate-300 dark:border-slate-600"
                  // unoptimized
                />
                <Button
                  variant="ghost"
                  size="icon"
                  type="button" // Важно: type="button" чтобы не сабмитить форму
                  className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/50 text-white hover:bg-black/70 z-10"
                  onClick={handleClearFile}
                >
                  <X size={16} />
                </Button>
              </div>
            ) : (
              <div className="mt-2 flex justify-center rounded-lg border border-dashed border-slate-400 dark:border-slate-600 bg-slate-100/50 dark:bg-slate-800/30 px-6 py-10 hover:border-sky-500">
                <div className="text-center">
                  <UploadCloud className="mx-auto h-12 w-12 text-slate-500" />
                  <div className="mt-4 flex text-sm leading-6 text-slate-600 dark:text-slate-400">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer rounded-md font-semibold text-sky-600 hover:text-sky-500"
                    >
                      <span>Выберите файл</span>
                      <Input
                        id="file-upload"
                        name="file"
                        type="file"
                        required
                        accept="image/*"
                        className="sr-only"
                        onChange={handleFileChange}
                      />
                    </label>
                    <p className="pl-1">или перетащите сюда</p>
                  </div>
                  <p className="text-xs leading-5 text-slate-500">
                    PNG, JPG, GIF до {MAX_FILE_SIZE_MB}МБ
                  </p>
                </div>
              </div>
            )}
            {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
          </div>

          {showCategorySelect && (
            <div>
              <label
                htmlFor="category"
                className="font-medium text-sm text-slate-800 dark:text-slate-100"
              >
                Категория
              </label>
              <select
                name="category"
                id="category"
                required
                className={selectStyles}
                defaultValue=""
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="" disabled>
                  Выберите категорию
                </option>
                {categories.map((cat) => (
                  <option key={cat.key} value={cat.key}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          )}

{uploadType === 'tourism' && selectedCategory === 'EVENTS' && (
            <div>
              <label
                htmlFor="endDate"
                className="font-medium text-sm text-slate-800 dark:text-slate-100"
              >
                Дата окончания события
              </label>
              <Input
                id="endDate"
                name="endDate"
                type="date"
                required
                className={inputStyles}
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                После этой даты событие будет скрыто и удалено задачей очистки.
              </p>
            </div>
          )}

          <div>
            <label
              htmlFor="caption"
              className="font-medium text-sm text-slate-800 dark:text-slate-100"
            >
              Заголовок
            </label>
            <Input
              id="caption"
              // Используем единое имя 'caption', в action разберемся
              name="caption"
              type="text"
              placeholder="Например, 'Уютная кофейня'"
              required
              className={inputStyles}
              maxLength={100}
            />
          </div>

          <div>
            <label
              htmlFor="description"
              className="font-medium text-sm text-slate-800 dark:text-slate-100"
            >
              Описание
            </label>
            <Textarea
              id="description"
              name="description"
              placeholder="Расскажите подробнее..."
              required
              className={inputStyles}
              rows={4}
            />
          </div>

          <Button type="submit" disabled={isUploading || !!error || !file} className="w-full">
            {isUploading ? 'Загрузка...' : 'Отправить на модерацию'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
